import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import { VisitFormModel } from "./models/visit-form.model";
import { EntityService } from "src/app/pages/entity/services/entity.service";
import { environment } from "src/environments/environment";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Deals } from "src/app/pages/deals/models/deals.model";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { EventService } from "src/app/core/services/event.service";
import { FilesService } from "src/app/core/services/files.service";

@Component({
  selector: "app-visit-form",
  templateUrl: "./visit-form.component.html",
  styleUrls: ["./visit-form.component.scss"],
})
export class VisitFormComponent implements OnInit, OnDestroy {
  @Input() deal: Deals | null = null;
  @Input() dealType: any;
  @Input() isEdit: boolean = false;
  /**
   * A visita já foi marcada à parte e isto é só o documento. Sem isto o
   * backend criava uma segunda VisitProperty e a visita aparecia duplicada.
   */
  @Input() skipVisitCreation: boolean = false;
  /**
   * Imóvel da ficha, quando não é o alvo do negócio. Uma visita pode ter sido
   * marcada para outro imóvel associado — sem isto a ficha saía sempre com o
   * alvo, ou seja, com o imóvel errado.
   */
  @Input() presetProperty: any = null;
  /**
   * Modo sem interface: os dados já foram recolhidos noutro sítio (o modal de
   * agendar visita) e aqui só se gera e envia o PDF. Sem isto, pedir a ficha
   * abria um segundo modal a repetir campos que o utilizador acabou de
   * preencher.
   */
  @Input() headless: boolean = false;
  /** Valores da ficha quando `headless` — o formulário nunca é mostrado. */
  @Input() presetValues: {
    date?: any;
    time?: any;
    agent?: string;
    listingAgent?: string;
    clientName?: string;
    clientContact?: string;
  } | null = null;

  visitForm!: FormGroup;
  visit!: VisitFormModel | null;
  entityData: any;

  @ViewChild("visitFormModal") visitFormModal: any;
  spinnerLoading: boolean = false;

  submitted: boolean = false;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private entityService: EntityService,
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private eventService: EventService,
    private fileService: FilesService,
  ) {}

  ngOnInit(): void {
    this.entityService.getEntityForProposal().subscribe((data) => {
      this.entityData = data;

      // Sem interface: os dados vieram já preenchidos, gera-se logo.
      if (this.headless) {
        this.initForm();
        this.visitForm.patchValue({
          ...(this.presetValues ?? {}),
        });
        this.generateVisitForm();
        return;
      }

      if (!this.isEdit) {
        this.openModal();
      } else {
        this.fileService
          .getVisitForm(this.deal?.id, this.dealType)
          .subscribe((data) => {
            if (data) {
              const date = new Date(data.visitDate);
              const valueDate = date.toISOString().split("T")[0];
              const value = date.toTimeString().split(" ")[0].slice(0, 5);
              this.visitForm = this.formBuilder.group({
                date: [valueDate, Validators.required],
                time: [value, Validators.required],
                agent: [data?.responsible || "", Validators.required],
                listingAgent: [data?.listingAgent || "", Validators.required],
                clientName: [
                  data?.clientName || this.deal?.name,
                  Validators.required,
                ],
                clientContact: [
                  data?.clientContact || this.deal?.phone,
                  Validators.required,
                ],
              });
              this.openModal(false);
            }
          });
      }
    });

    // Sem unsubscribe, cada abertura do formulário acumulava um handler
    // global que fazia dismissAll() de TODOS os modais a cada visita gerada.
    const visitGeneratedSub = this.eventService.subscribe(
      "visit-generated",
      () => {
        this.spinnerLoading = false;
        this.visit = null;

        // Sem interface não há modal para fechar — mas quem montou este
        // componente continua à espera do aviso de que acabou.
        if (this.headless) {
          this.eventService.broadcast("visit-form-closed");
          return;
        }

        this.modalService.dismissAll();
      },
    );
    this.subscriptions.add(visitGeneratedSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openModal(initForm: boolean = true) {
    if (initForm) this.initForm();
    this.submitted = false;

    let modal = this.modalService.open(this.visitFormModal, {
      size: "lg",
      centered: true,
      backdropClass: "modal-z",
      windowClass: "modal-z",
    });
    modal.closed.subscribe(() => {
      this.visit = null;
      this.submitted = false;
      this.eventService.broadcast("visit-form-closed");
    });
    modal.dismissed.subscribe(() => {
      this.visit = null;
      this.submitted = false;
      this.eventService.broadcast("visit-form-closed");
    });
  }

  initForm() {
    // Default: a próxima hora certa. O valor anterior (uma hora ATRÁS) criava
    // visitas já no passado, que nunca contavam como "visita futura" nas
    // validações de etapa.
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    const valueDate = now.toISOString().split("T")[0];
    const value = now.toTimeString().split(" ")[0].slice(0, 5);
    const externalProperty = this.deal?.externalProperty;

    this.visitForm = this.formBuilder.group({
      date: [valueDate, [Validators.required]],
      time: [value, [Validators.required]],
      agent: [this.deal?.agent?.name || "", [Validators.required]],
      listingAgent: [
        this.deal?.property?.listingAgent || externalProperty?.agentName || "",
        [Validators.required],
      ],
      clientName: [
        this.deal?.contact?.name || this.deal?.name,
        [Validators.required],
      ],
      clientContact: [
        this.deal?.contact?.phoneNumber || this.deal?.phone,
        [Validators.required],
      ],
    });
  }

  get form() {
    return this.visitForm.controls;
  }

  generateVisitForm() {
    this.submitted = true;

    if (this.visitForm.invalid) {
      this.visitForm.markAllAsTouched();
      return;
    }

    this.spinnerLoading = true;
    // O imóvel da ficha: o da visita, quando indicado, senão o alvo.
    const source = this.presetProperty ?? this.deal?.property;
    const externalProperty = source?.isExternal ? source : null;

    this.visit = {
      date: this.visitForm.value.date,
      time: this.visitForm.value.time,
      agentName: this.visitForm.value.agent,
      agentEmail: this.deal?.agent?.email || "",
      agentId: this.deal?.agent?.id,
      clientName: this.visitForm.value.clientName,
      clientContact: this.visitForm.value.clientContact,
      clientEmail: this.deal?.contact?.email,
      dealId: this.deal?.id,
      leadId: this.deal?.leadId,
      contactId: this.deal?.contact?.id,
      type: this.dealType,
      entity: this.deal?.entity,
      // Quando a visita já foi marcada no modal de agendamento, isto é só o
      // documento — o backend não deve criar uma segunda VisitProperty.
      skipVisitCreation: this.skipVisitCreation,
      property: {
        id: source?.id,
        externalPropertyId: externalProperty?.id ?? undefined,
        isExternal: !!externalProperty,
        address: externalProperty?.address || source?.location?.address || "",
        energeticCertification: source?.energeticCertification || "",
        price: externalProperty?.price || source?.price || 0,
        reference:
          externalProperty?.reference ||
          externalProperty?.ref ||
          source?.ref ||
          source?.reference ||
          "",
        symbol: externalProperty?.symbol || source?.symbol || "",
        title: externalProperty?.title || source?.title || "",
        businessType: source?.businessType || "",
        grossArea: source?.grossArea || 0,
        image: externalProperty?.image || source?.image || "",
        listingAgent: this.visitForm?.value?.listingAgent || "",
        yearBuilt: source?.yearBuilt || "",
        storeData: source?.storeData,
        owner: source?.owner,
        contractNumber: source?.contractNumber,
        agency: externalProperty?.agency ?? undefined,
      },
    };
  }
}
